<template>
  <div>
    <Nav></Nav>
    <b-container fluid class="mt-3">
      <!-- Header -->
      <b-row>
        <b-col class="text-left">
          <b-button
            variant="link"
            @click="$router.push('/stock-locations')"
            class="p-0 mb-2">
            ← Volver a Ubicaciones
          </b-button>
          <h1>{{ currentLocation.complete_name || currentLocation.name }}</h1>
        </b-col>
      </b-row>

      <!-- Action Buttons -->
      <b-row class="mb-3">
        <b-col>
          <b-button-group>
            <b-button
              variant="warning"
              size="sm"
              @click="openEditModal">
              Editar
            </b-button>
            <b-button
              variant="danger"
              size="sm"
              @click="confirmDelete">
              Eliminar
            </b-button>
            <b-button
              variant="primary"
              size="sm"
              @click="getLocation">
              Actualizar
            </b-button>
          </b-button-group>
        </b-col>
      </b-row>

      <!-- Loading State -->
      <b-row v-if="loading">
        <b-col class="text-center">
          <b-spinner variant="primary"></b-spinner>
          <p>Cargando...</p>
        </b-col>
      </b-row>

      <!-- Location Details Card -->
      <b-card v-if="!loading" class="mb-3">
        <b-row>
          <b-col>
            <h3>Información General</h3>
          </b-col>
        </b-row>
        <b-row>
          <b-col lg="6" md="12">
            <table class="table table-borderless table-sm">
              <tbody>
                <tr>
                  <td class="font-weight-bold">ID:</td>
                  <td>{{ currentLocation.id }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">Nombre:</td>
                  <td>{{ currentLocation.name }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">Nombre Completo:</td>
                  <td>{{ currentLocation.complete_name }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">Tipo de Ubicación:</td>
                  <td>
                    <b-badge :variant="getTypeVariant(currentLocation.type_usage)">
                      {{ getTypeLabel(currentLocation.type_usage) }}
                    </b-badge>
                  </td>
                </tr>
                <tr>
                  <td class="font-weight-bold">Código de Barras:</td>
                  <td>{{ currentLocation.barcode || '-' }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">Estado:</td>
                  <td>
                    <b-badge :variant="currentLocation.status ? 'success' : 'danger'">
                      {{ currentLocation.status ? 'Activo' : 'Inactivo' }}
                    </b-badge>
                  </td>
                </tr>
              </tbody>
            </table>
          </b-col>
          <b-col lg="6" md="12">
            <table class="table table-borderless table-sm">
              <tbody>
                <tr>
                  <td class="font-weight-bold">Ruta Padre:</td>
                  <td>{{ currentLocation.parent_path || '-' }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">Ubicación Padre ID:</td>
                  <td>{{ currentLocation.location_id || '-' }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">Sucursal ID:</td>
                  <td>{{ currentLocation.sucursal_id || '-' }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">Company ID:</td>
                  <td>{{ currentLocation.company_id || '-' }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">Comentario:</td>
                  <td>{{ currentLocation.comment || '-' }}</td>
                </tr>
              </tbody>
            </table>
          </b-col>
        </b-row>
      </b-card>

      <!-- Special Characteristics Card -->
      <b-card v-if="!loading" class="mb-3">
        <b-row>
          <b-col>
            <h3>Características Especiales</h3>
          </b-col>
        </b-row>
        <b-row>
          <b-col lg="4" md="6" cols="12">
            <b-form-checkbox
              v-model="currentLocation.is_scrap_location"
              disabled>
              Ubicación de Chatarra
            </b-form-checkbox>
          </b-col>
          <b-col lg="4" md="6" cols="12">
            <b-form-checkbox
              v-model="currentLocation.is_return_location"
              disabled>
              Ubicación de Devoluciones
            </b-form-checkbox>
          </b-col>
          <b-col lg="4" md="6" cols="12">
            <b-form-checkbox
              v-model="currentLocation.is_replenish_location"
              disabled>
              Ubicación de Reabastecimiento
            </b-form-checkbox>
          </b-col>
        </b-row>
      </b-card>

      <!-- Stock Quantities Card -->
      <b-card v-if="!loading && stockQuantities.length > 0" class="mb-3">
        <b-row>
          <b-col>
            <h3>Existencias en esta Ubicación</h3>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <b-form-group label="Buscar producto:">
              <b-form-input
                type="search"
                v-model="stockSearch"
                debounce="500"
                placeholder="Buscar por código o nombre...">
              </b-form-input>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <b-table
              :filter="stockSearch"
              striped
              hover
              small
              :items="stockQuantities"
              :fields="stockFields"
              responsive>
              <template #table-busy>
                <div class="text-center text-danger my-2">
                  <b-spinner class="align-middle"></b-spinner>
                  <strong>Cargando...</strong>
                </div>
              </template>
            </b-table>
          </b-col>
        </b-row>
      </b-card>

      <!-- Child Locations Card -->
      <b-card v-if="!loading && childLocations.length > 0" class="mb-3">
        <b-row>
          <b-col>
            <h3>Ubicaciones Hijas</h3>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <b-table
              striped
              hover
              small
              :items="childLocations"
              :fields="childLocationFields"
              responsive>
              <template #cell(type_usage)="data">
                <b-badge :variant="getTypeVariant(data.item.type_usage)">
                  {{ getTypeLabel(data.item.type_usage) }}
                </b-badge>
              </template>
              <template #cell(status)="data">
                <b-badge :variant="data.item.status ? 'success' : 'danger'">
                  {{ data.item.status ? 'Activo' : 'Inactivo' }}
                </b-badge>
              </template>
              <template #cell(buttons)="data">
                <b-button
                  size="sm"
                  variant="info"
                  @click="$router.push('/stock-location/' + data.item.id)">
                  Ver
                </b-button>
              </template>
            </b-table>
          </b-col>
        </b-row>
      </b-card>

      <!-- Edit Modal -->
      <b-modal
        id="modal-edit-location"
        title="Editar Ubicación"
        :hide-footer="true"
        size="lg"
        @hidden="getLocation">
        <b-form @submit.prevent="updateLocation">
          <b-row>
            <b-col lg="6" cols="12">
              <b-form-group
                label="Nombre*"
                description="Nombre de la ubicación">
                <b-form-input
                  v-model="editForm.name"
                  required
                  placeholder="Ej: Almacén Principal">
                </b-form-input>
              </b-form-group>
            </b-col>
            <b-col lg="6" cols="12">
              <b-form-group
                label="Nombre Completo"
                description="Nombre completo o ruta">
                <b-form-input
                  v-model="editForm.complete_name"
                  placeholder="Ej: Almacén / Almacén Principal">
                </b-form-input>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col lg="6" cols="12">
              <b-form-group
                label="Tipo de Ubicación*"
                description="Tipo de uso de la ubicación">
                <b-form-select
                  v-model="editForm.type_usage"
                  :options="typeOptions"
                  required>
                </b-form-select>
              </b-form-group>
            </b-col>
            <b-col lg="6" cols="12">
              <b-form-group
                label="Código de Barras"
                description="Código de barras de la ubicación">
                <b-form-input
                  v-model="editForm.barcode"
                  placeholder="Ej: LOC001">
                </b-form-input>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col lg="6" cols="12">
              <b-form-group
                label="Ubicación Padre ID"
                description="ID de la ubicación padre (opcional)">
                <b-form-input
                  v-model.number="editForm.location_id"
                  type="number"
                  placeholder="ID de ubicación padre">
                </b-form-input>
              </b-form-group>
            </b-col>
            <b-col lg="6" cols="12">
              <b-form-group
                label="Sucursal ID"
                description="Sucursal asociada (opcional)">
                <b-form-input
                  v-model.number="editForm.sucursal_id"
                  type="number"
                  placeholder="ID de sucursal">
                </b-form-input>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col cols="12">
              <b-form-group
                label="Comentario"
                description="Notas adicionales sobre la ubicación">
                <b-form-textarea
                  v-model="editForm.comment"
                  rows="3"
                  placeholder="Ej: Ubicación para productos terminados">
                </b-form-textarea>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col lg="4" cols="12">
              <b-form-checkbox v-model="editForm.is_scrap_location">
                Ubicación de Chatarra
              </b-form-checkbox>
            </b-col>
            <b-col lg="4" cols="12">
              <b-form-checkbox v-model="editForm.is_return_location">
                Ubicación de Devoluciones
              </b-form-checkbox>
            </b-col>
            <b-col lg="4" cols="12">
              <b-form-checkbox v-model="editForm.is_replenish_location">
                Ubicación de Reabastecimiento
              </b-form-checkbox>
            </b-col>
          </b-row>

          <b-row class="mt-3">
            <b-col lg="12" cols="12">
              <b-form-checkbox v-model="editForm.status">
                Activo
              </b-form-checkbox>
            </b-col>
          </b-row>

          <b-row class="mt-3">
            <b-col>
              <b-button
                type="submit"
                variant="success"
                class="mr-2"
                :disabled="saving">
                <b-spinner small v-if="saving"></b-spinner>
                {{ saving ? 'Guardando...' : 'Guardar' }}
              </b-button>
              <b-button
                variant="secondary"
                @click="$bvModal.hide('modal-edit-location')">
                Cancelar
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </b-modal>
    </b-container>
  </div>
</template>

<script>
import axios from "axios";
import Nav from "../components/Nav.vue";

export default {
  name: "StockLocation",
  components: { Nav },
  data() {
    return {
      loading: false,
      saving: false,
      stockSearch: "",
      currentLocation: {
        id: null,
        location_id: null,
        name: "",
        complete_name: "",
        type_usage: "",
        parent_path: "",
        barcode: null,
        comment: "",
        is_scrap_location: false,
        is_return_location: false,
        is_replenish_location: false,
        status: false,
        sucursal_id: null,
        company_id: null
      },
      editForm: {},
      stockQuantities: [],
      childLocations: [],
      stockFields: [
        { key: "supply.id", label: "ID", sortable: true },
        { key: "supply.code", label: "Código", sortable: true },
        { key: "supply.name", label: "Nombre", sortable: true },
        { key: "quantity", label: "Cantidad", sortable: true },
        { key: "reserved_quantity", label: "Reservado", sortable: true }
      ],
      childLocationFields: [
        { key: "id", label: "ID", sortable: true },
        { key: "name", label: "Nombre", sortable: true },
        { key: "complete_name", label: "Nombre Completo", sortable: true },
        { key: "type_usage", label: "Tipo", sortable: true },
        { key: "status", label: "Estado", sortable: true },
        { key: "buttons", label: "Acciones" }
      ],
      typeOptions: [
        { value: "supplier", text: "Proveedor" },
        { value: "view", text: "Vista" },
        { value: "internal", text: "Interno" },
        { value: "customer", text: "Cliente" },
        { value: "inventory", text: "Inventario" },
        { value: "production", text: "Producción" },
        { value: "transit", text: "Tránsito" }
      ]
    };
  },
  mounted() {
    this.getLocation();
    this.getStockQuantities();
    this.getChildLocations();
  },
  methods: {
    getLocation() {
      let that = this;
      this.loading = true;
      axios.get("/stocklocations/" + this.$route.params.id)
        .then(res => {
          that.currentLocation = res.data;
          that.loading = false;
        })
        .catch(err => {
          that.loading = false;
          that.makeToast("Error", err.response?.data || err.message, "danger");
        });
    },
    getStockQuantities() {
      let that = this;
      axios.get("/stock/quantities")
        .then(res => {
          // Filter quantities for this location
          that.stockQuantities = res.data.filter(x =>
            x.location && x.location.id == that.$route.params.id &&
            (x.quantity > 0 || x.reserved_quantity > 0)
          );
        })
        .catch(err => {
          console.error("Error loading stock quantities:", err);
        });
    },
    getChildLocations() {
      // This would require a backend endpoint to get child locations
      // For now, we'll leave it empty and it can be implemented later
      // let that = this;
      // axios.get("/stocklocations/" + this.$route.params.id + "/children")
      //   .then(res => {
      //     that.childLocations = res.data;
      //   })
      //   .catch(err => {
      //     console.error("Error loading child locations:", err);
      //   });
    },
    openEditModal() {
      this.editForm = { ...this.currentLocation };
      this.$bvModal.show('modal-edit-location');
    },
    updateLocation() {
      let that = this;
      this.saving = true;
      axios.put("/stocklocations/" + this.editForm.id, this.editForm)
        .then(() => {
          that.saving = false;
          that.makeToast("Éxito", "Ubicación actualizada correctamente", "success");
          that.$bvModal.hide('modal-edit-location');
          that.getLocation();
        })
        .catch(err => {
          that.saving = false;
          that.makeToast("Error", err.response?.data || err.message, "danger");
        });
    },
    confirmDelete() {
      this.$bvModal.msgBoxConfirm(
        `¿Está seguro de eliminar la ubicación "${this.currentLocation.name}"? Esta acción no se puede deshacer.`,
        {
          title: 'Confirmar Eliminación',
          size: 'md',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: 'Eliminar',
          cancelTitle: 'Cancelar',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true
        }
      )
      .then(value => {
        if (value) {
          this.deleteLocation();
        }
      })
      .catch(err => {
        console.error(err);
      });
    },
    deleteLocation() {
      let that = this;
      axios.delete("/stocklocations/" + this.currentLocation.id)
        .then(() => {
          that.makeToast("Éxito", "Ubicación eliminada correctamente", "success");
          that.$router.push('/stock-locations');
        })
        .catch(err => {
          that.makeToast("Error", err.response?.data || err.message, "danger");
        });
    },
    getTypeVariant(type) {
      const variants = {
        supplier: "secondary",
        view: "info",
        internal: "primary",
        customer: "success",
        inventory: "warning",
        production: "dark",
        transit: "light"
      };
      return variants[type] || "secondary";
    },
    getTypeLabel(type) {
      const labels = {
        supplier: "Proveedor",
        view: "Vista",
        internal: "Interno",
        customer: "Cliente",
        inventory: "Inventario",
        production: "Producción",
        transit: "Tránsito"
      };
      return labels[type] || type;
    },
    makeToast(title, content, variant) {
      this.$bvToast.toast(content, {
        title,
        variant,
        autoHideDelay: 5000,
        appendToast: true,
      });
    }
  }
};
</script>

<style scoped>
</style>
